import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ChevronLeft as CollapseIcon,
  ChevronRight as ExpandIcon,
} from '@mui/icons-material';

import { useSession } from 'store';
import { Navigation } from 'components';
import navCfg from './cfg';

const NAV_WIDTH = 220;
const NAV_BRIEF_WIDTH = 64;

const openedMixin = theme => ({
  width: NAV_WIDTH,
  overflowX: 'hidden',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
});

const closedMixin = theme => ({
  width: NAV_BRIEF_WIDTH,
  overflowX: 'hidden',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
});

const DesktopDrawer = styled(Drawer, {
  shouldForwardProp: prop => prop !== 'brief',
})(({ theme, brief }) => ({
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(brief ? closedMixin(theme) : openedMixin(theme)),
  '& .MuiDrawer-paper': {
    position: 'relative',
    height: '100%',
    boxSizing: 'border-box',
    ...(brief ? closedMixin(theme) : openedMixin(theme)),
  },
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  minHeight: 48,
  padding: theme.spacing(0, 1),
}));

const NavBar = ({openDrawer, onDrawerClose, brief, onBriefToggle, hide, styles, ...rest}) => {
  const session = useSession((state) => state.session);
  const loc = useLocation();
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'));

  const isAdmin = session.role === 'admin';
  const items = navCfg.filter(item => isAdmin || !item.needAdmin);

  // close nav bar if url changed
  useEffect(
    () => void(openDrawer && onDrawerClose?.()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loc.key]
  );

  // only use drawer on mobile for 'auto' hide
  const useDrawer = ({always: true, never: false, auto: !lgUp})[hide];
  const effectiveBrief = !useDrawer && brief;

  const navbarContent = (
    <Box
      sx={{
        padding: effectiveBrief ? 1 : 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: effectiveBrief ? 'center' : 'stretch',
        minHeight: 'fit-content',
        boxSizing: 'border-box'
      }}
    >
      <Navigation items={items} brief={effectiveBrief} />
    </Box>
  );

  return !useDrawer ? (
    <DesktopDrawer
      {...rest}
      variant="permanent"
      brief={effectiveBrief}
      sx={styles}
    >
      <DrawerHeader sx={{justifyContent: effectiveBrief ? 'center' : 'flex-end'}}>
        <Tooltip title={effectiveBrief ? 'Expand menu' : 'Collapse menu'} placement="right">
          <IconButton
            size="small"
            aria-label={effectiveBrief ? 'Expand menu' : 'Collapse menu'}
            onClick={onBriefToggle}
          >
            {effectiveBrief ? <ExpandIcon fontSize="small" /> : <CollapseIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </DrawerHeader>
      <Divider />
      {navbarContent}
    </DesktopDrawer>
  ) : (
    <Drawer
      anchor="left"
      onClose={onDrawerClose}
      open={openDrawer}
      variant="temporary"
    >
      <Box
        {...rest}
        sx={{height: '100%', overflowY: 'auto', width: NAV_WIDTH, ...styles}}
      >
        {navbarContent}
      </Box>
    </Drawer>
  );
};

NavBar.propTypes = {
  styles: PropTypes.object,
  onDrawerClose: PropTypes.func,
  openDrawer: PropTypes.bool,
  brief: PropTypes.bool,
  onBriefToggle: PropTypes.func,
  hide: PropTypes.oneOf(['always', 'never', 'auto']),
};

export default NavBar;
